import { reactive } from "vue";
export const toasts = reactive([]);
let _nextId = 0;
const _timers = new Map();
export function showToast(options) {
    const id = ++_nextId;
    const countdown = options.countdown ?? 0;
    const countdownEnd = countdown > 0 ? Date.now() + countdown * 1000 : 0;
    const countdownKeepOpen = options.countdownKeepOpen ?? false;
    const duration = countdown > 0 ? countdown * 1000 : (options.duration ?? 4000);
    const countdownAction = options.countdownAction;
    toasts.push({
        id,
        title: options.title ?? "",
        message: options.message,
        type: options.type ?? "primary",
        duration,
        position: options.position ?? "top-end",
        icon: options.icon ?? "",
        actions: options.actions ?? [],
        closable: options.closable ?? true,
        showTimestamp: options.showTimestamp ?? false,
        blocking: options.blocking ?? false,
        spinner: options.spinner ?? false,
        countdown,
        countdownEnd,
        countdownKeepOpen,
        countdownAction,
        timestamp: Date.now(),
    });
    if (duration > 0) {
        _timers.set(id, setTimeout(() => {
            if (countdownAction) {
                countdownAction(() => removeToast(id));
            }
            else if (!countdownKeepOpen) {
                removeToast(id);
            }
        }, duration));
    }
    return id;
}
export function updateToast(id, patch) {
    const toast = toasts.find((t) => t.id === id);
    if (!toast)
        return;
    if (patch.title !== undefined)
        toast.title = patch.title;
    if (patch.message !== undefined)
        toast.message = patch.message;
    if (patch.type !== undefined)
        toast.type = patch.type;
    if (patch.icon !== undefined)
        toast.icon = patch.icon;
    if (patch.actions !== undefined)
        toast.actions = patch.actions;
    if (patch.closable !== undefined)
        toast.closable = patch.closable;
    if (patch.position !== undefined)
        toast.position = patch.position;
    if (patch.showTimestamp !== undefined)
        toast.showTimestamp = patch.showTimestamp;
    if (patch.blocking !== undefined)
        toast.blocking = patch.blocking;
    if (patch.spinner !== undefined)
        toast.spinner = patch.spinner;
    if (patch.countdownAction !== undefined) {
        toast.countdownAction = patch.countdownAction;
    }
    if (patch.countdownKeepOpen !== undefined) {
        toast.countdownKeepOpen = patch.countdownKeepOpen;
    }
    if (patch.countdown !== undefined) {
        toast.countdown = patch.countdown;
        const existing = _timers.get(id);
        if (existing) {
            clearTimeout(existing);
            _timers.delete(id);
        }
        if (patch.countdown > 0) {
            toast.countdownEnd = Date.now() + patch.countdown * 1000;
            toast.duration = patch.countdown * 1000;
            const action = toast.countdownAction;
            const keepOpen = toast.countdownKeepOpen;
            _timers.set(id, setTimeout(() => {
                if (action)
                    action(() => removeToast(id));
                else if (!keepOpen)
                    removeToast(id);
            }, patch.countdown * 1000));
        }
        else {
            toast.countdownEnd = 0;
        }
    }
    else if (patch.duration !== undefined) {
        toast.duration = patch.duration;
        const existing = _timers.get(id);
        if (existing) {
            clearTimeout(existing);
            _timers.delete(id);
        }
        if (patch.duration > 0) {
            _timers.set(id, setTimeout(() => removeToast(id), patch.duration));
        }
    }
}
export function showOrUpdateToast(id, options) {
    if (id > 0 && toasts.find((t) => t.id === id)) {
        updateToast(id, options);
        return id;
    }
    else {
        return showToast(options);
    }
}
export function removeToast(id) {
    const idx = toasts.findIndex((t) => t.id === id);
    if (idx !== -1) {
        toasts.splice(idx, 1);
        const timer = _timers.get(id);
        if (timer) {
            clearTimeout(timer);
            _timers.delete(id);
        }
    }
}
export async function withToast(work, options) {
    const opts = typeof options === "string" ? { loading: options } : options;
    const loadingOpts = typeof opts.loading === "string"
        ? {
            message: opts.loading,
            type: "info",
            spinner: true,
            closable: false,
            duration: 0,
        }
        : {
            type: "info",
            spinner: true,
            closable: false,
            duration: 0,
            ...opts.loading,
        };
    const id = showToast(loadingOpts);
    try {
        const result = await work();
        if (opts.success !== undefined) {
            const raw = typeof opts.success === "function"
                ? opts.success(result)
                : opts.success;
            const patch = typeof raw === "string" ? { message: raw } : raw;
            updateToast(id, {
                spinner: false,
                closable: true,
                duration: 4000,
                type: "success",
                ...patch,
            });
        }
        else {
            removeToast(id);
        }
        return result;
    }
    catch (err) {
        const raw = typeof opts.error === "function" ? opts.error(err) : opts.error;
        const patch = typeof raw === "string"
            ? { message: raw }
            : (raw ?? { message: "An error has occurred." });
        updateToast(id, {
            spinner: false,
            closable: true,
            duration: 0,
            type: "danger",
            ...patch,
        });
        throw err;
    }
}
export function useToast() {
    return {
        toasts,
        showToast,
        updateToast,
        showOrUpdateToast,
        removeToast,
        withToast,
    };
}
